import HeaderAccountLink from '../components/HeaderAccountLink';
import HeaderNavDrawer from '../components/HeaderNavDrawer';
import LocalizationDisclosure from '../components/localization/LocalizationDisclosure';
import { SEARCH_PANEL_OFFSET_EVENT } from '../constants/header';
import Scroll from '../helpers/scroll';
import Search from '../components/search/Search';

export default class MainHeader {
  constructor(section) {
    this.section = section;
    this.headerEl = section.el.querySelector('header');

    const { settings } = section.data;

    if (settings.enable_fixed_header && this.headerEl) {
      this.scroll = new Scroll(this.onScroll, this.section.el);
      // The sticky wrapper keeps its full height even while the header is
      // translated off-screen, so it must not capture clicks itself — only its
      // interactive children (the header row and the fixed drawer/search
      // popout, which re-enable pointer events in navigation.css) may.
      this.section.el.className += ' sticky top-0 z-50 pointer-events-none';
      this.headerEl.className +=
        ' w-full bg-black border-b border-white transition-transform duration-150 ease-out will-change-transform pointer-events-auto';
    }

    new Search(section);
    this.navDrawer = new HeaderNavDrawer(section.el, this.headerEl);
    this.headerAccountLink = new HeaderAccountLink(section.el);
    LocalizationDisclosure.init(document.body);

    this._updateSearchPanelOffset = this._updateSearchPanelOffset.bind(this);
    this._handleScroll = this._handleScroll.bind(this);
    this._handleTransitionEnd = this._handleTransitionEnd.bind(this);

    this._updateSearchPanelOffset();
    window.addEventListener('resize', this._updateSearchPanelOffset);
    window.addEventListener('scroll', this._handleScroll, { passive: true });
    document.addEventListener(
      SEARCH_PANEL_OFFSET_EVENT,
      this._updateSearchPanelOffset,
    );
    this.headerEl?.addEventListener('transitionend', this._handleTransitionEnd);
  }

  _getAnnouncementSection = () => {
    const announcementSection = this.section.el.previousElementSibling;

    if (!announcementSection?.querySelector('[data-announcement-bar]')) {
      return null;
    }

    return announcementSection;
  };

  _getSearchPanelOffset = () => {
    const rects = [];

    const announcementSection = this._getAnnouncementSection();
    if (announcementSection) {
      rects.push(announcementSection.getBoundingClientRect().bottom);
    }

    if (this.headerEl?.classList.contains('-translate-y-full')) {
      return Math.max(...rects, 0);
    }

    if (this.headerEl) {
      rects.push(this.headerEl.getBoundingClientRect().bottom);
    }

    return Math.max(...rects, 0);
  };

  _updateSearchPanelOffset = () => {
    const bottom = this._getSearchPanelOffset();

    if (bottom <= 0) return;

    this.section.el.style.setProperty('--header-bottom', `${bottom}px`);
  };

  _handleScroll = () => {
    this._updateSearchPanelOffset();
  };

  _handleTransitionEnd = event => {
    if (event.target !== this.headerEl || event.propertyName !== 'transform') {
      return;
    }

    this._updateSearchPanelOffset();
  };

  onScroll = direction => {
    if (this.navDrawer?.isOpen) return;

    this.headerEl?.classList.toggle('-translate-y-full', direction === 'down');
  };

  onSectionUnload() {
    window.removeEventListener('resize', this._updateSearchPanelOffset);
    window.removeEventListener('scroll', this._handleScroll);
    document.removeEventListener(
      SEARCH_PANEL_OFFSET_EVENT,
      this._updateSearchPanelOffset,
    );
    this.headerEl?.removeEventListener(
      'transitionend',
      this._handleTransitionEnd,
    );

    this.headerAccountLink?.destroy();
    this.headerAccountLink = null;

    if (this.scroll) {
      this.scroll.destroy();
      this.scroll = null;
    }
  }
}
